import { beginCartLinesUpdate } from './standard-cart-events.js';
import { preserveSectionStyles } from './section-styles.js';

const STORAGE_KEY = 'shopify_wishlist';
const RECENTLY_VIEWED_STORAGE_KEY = 'viewedProducts';
const ROOT = window.Shopify?.routes?.root || '/';
const API_ROOT = `${ROOT}apps/gadget`;
const CARD_SECTION_ID = 'ch-wishlist-product-card';

const state = {
  items: [],
  serverLoaded: false,
  syncing: false,
  countObserver: null,
  countUpdateFrame: null,
  prunedKeys: new Set(),
  aliReviewsRefreshFrame: null,
  aliReviewsStarRatingPromise: null,
};

function readConfig() {
  return window.CH_WISHLIST_CONFIG || {
    isLoggedIn: false,
    metafieldItems: [],
  };
}

function normalizeItem(item) {
  if (!item) return null;
  if (typeof item === 'string') return { handle: item, variantId: null };
  const handle = item.handle || item.productHandle;
  const variantId = item.variantId || item.variant_id || null;
  if (!handle && !variantId) return null;
  return {
    handle,
    variantId: variantId ? String(variantId) : null,
    productId: item.productId ? String(item.productId) : null,
    title: item.title || item.productTitle || null,
    available: item.available,
  };
}

function dedupeItems(items) {
  const seen = new Set();
  const output = [];

  for (const rawItem of items || []) {
    const item = normalizeItem(rawItem);
    if (!item) continue;
    const key = item.variantId || item.handle;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    output.push(item);
  }

  return output;
}

function readLocalItems() {
  try {
    return dedupeItems(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
  } catch (_error) {
    return [];
  }
}

function writeLocalItems(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(dedupeItems(items)));
}

function itemKey(item) {
  return item?.variantId || item?.handle;
}

function itemsAreSame(leftItems, rightItems) {
  if (leftItems.length !== rightItems.length) return false;

  return leftItems.every((item, index) => itemKey(item) === itemKey(rightItems[index]));
}

function persistPrunedItems(prunedItems) {
  if (!prunedItems.length) return;

  const config = readConfig();
  if (!config.isLoggedIn) {
    writeLocalItems(state.items);
    return;
  }

  prunedItems.forEach((item) => {
    if (!item.variantId) return;

    const key = itemKey(item);
    if (!key || state.prunedKeys.has(key)) return;

    state.prunedKeys.add(key);
    requestWishlist('/wishlist', {
      method: 'DELETE',
      body: JSON.stringify({ variantId: item.variantId }),
    }).catch(() => {
      state.prunedKeys.delete(key);
    });
  });
}

function readRecentlyViewedIds() {
  try {
    return JSON.parse(localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY) || '[]').filter(Boolean).slice(0, 4);
  } catch (_error) {
    return [];
  }
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
}

function getCartAddUrl() {
  return window.Theme?.routes?.cart_add_url || `${ROOT}cart/add.js`;
}

async function openCartDrawer() {
  if (typeof window.Shopify?.actions?.openCart === 'function') {
    await window.Shopify.actions.openCart();
    return;
  }

  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!cartDrawer) return;

  if (typeof cartDrawer.open === 'function') {
    cartDrawer.open();
    return;
  }

  cartDrawer.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
}

function getAliReviewsPublicPath() {
  if (window.__AR_PUBLIC_PATH) return window.__AR_PUBLIC_PATH;

  const script = Array.from(document.scripts).find((item) => {
    return (
      /\/ali-reviews-\d+\/assets\/(?:alireviews|alr-\d+)-/.test(item.src) ||
      (item.src.includes('/ali-reviews-') && item.src.includes('/assets/alireviews.min.js'))
    );
  });

  if (!script?.src) return null;

  return script.src.slice(0, script.src.lastIndexOf('/') + 1);
}

function loadAliReviewsStarRatingModule() {
  const existingModule = window['__alr_app__alireviews-star-rating-block'];
  if (existingModule?.default) return Promise.resolve(existingModule);
  if (state.aliReviewsStarRatingPromise) return state.aliReviewsStarRatingPromise;

  const publicPath = getAliReviewsPublicPath();
  if (!publicPath) return Promise.resolve(null);

  state.aliReviewsStarRatingPromise = new Promise((resolve) => {
    const existingScript = document.getElementById('ch-alireviews-star-rating-block');
    if (existingScript instanceof HTMLScriptElement) {
      existingScript.addEventListener('load', () => resolve(window['__alr_app__alireviews-star-rating-block'] || null), {
        once: true,
      });
      existingScript.addEventListener('error', () => resolve(null), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'ch-alireviews-star-rating-block';
    script.src = `${publicPath}alireviews-star-rating-block.min.js`;
    script.type = 'text/javascript';
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.fetchPriority = 'low';
    script.addEventListener('load', () => resolve(window['__alr_app__alireviews-star-rating-block'] || null), {
      once: true,
    });
    script.addEventListener('error', () => resolve(null), { once: true });
    document.body.append(script);
  });

  return state.aliReviewsStarRatingPromise;
}

function refreshAliReviewsStarRatings(container = document) {
  if (!window.__AR_WIDGET_REVIEW_STAR_RATING) return;
  if (!container.querySelector('.alireviews-review-star-rating:not([data-status="initialized"])')) return;
  if (state.aliReviewsRefreshFrame) return;

  state.aliReviewsRefreshFrame = requestAnimationFrame(() => {
    const run = () => {
      state.aliReviewsRefreshFrame = null;
      loadAliReviewsStarRatingModule()
        .then((module) => {
          module?.default?.();
        })
        .catch(() => {});
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 1000 });
    } else {
      setTimeout(run, 0);
    }
  });
}

function refreshHomepageAliReviewsNearViewport() {
  const sections = Array.from(
    document.querySelectorAll("#MainContent > [id$='__section_RR9EXH'], #MainContent > [id$='__section_xCpcAd']")
  ).filter((section) => section.querySelector('.alireviews-review-star-rating'));

  if (!sections.length) return;

  if (!('IntersectionObserver' in window)) {
    sections.forEach((section) => refreshAliReviewsStarRatings(section));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        refreshAliReviewsStarRatings(entry.target);
      });
    },
    { rootMargin: '600px 0px' }
  );

  sections.forEach((section) => observer.observe(section));
}

async function parseCartAddResponse(response) {
  const responseText = await response.text();
  let data = {};

  try {
    data = responseText ? JSON.parse(responseText) : {};
  } catch (_error) {}

  if (response.ok && !data.status) return data;

  const message = data.description || data.message || `Unable to add wishlist items to cart (${response.status})`;
  const error = new Error(message);
  error.status = response.status;
  error.data = data;
  throw error;
}

function shouldRetryCartAddIndividually(error) {
  return error?.status === 403 || error?.status >= 500;
}

async function addCartItemsInBatch(items, sectionIds) {
  const response = await fetch(getCartAddUrl(), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      items,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  });

  return parseCartAddResponse(response);
}

async function addCartItemsOneAtATime(items, sectionIds) {
  let lastResponse = {};

  for (const item of items) {
    const formData = new FormData();
    formData.append('id', item.id);
    formData.append('quantity', String(item.quantity || 1));
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);

    const response = await fetch(getCartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    lastResponse = await parseCartAddResponse(response);
  }

  return lastResponse;
}

function createRecentProductCard(product) {
  if (!product?.url || !product?.title) return null;

  const item = document.createElement('li');
  item.className = 'ch-wishlist__recent-card';

  const link = document.createElement('a');
  link.href = product.url;

  const imageWrapper = document.createElement('span');
  imageWrapper.className = 'ch-wishlist__recent-card-image';

  if (product.image) {
    const image = document.createElement('img');
    image.loading = 'lazy';
    image.alt = product.title;
    image.src = product.image;
    imageWrapper.append(image);
  }

  const title = document.createElement('span');
  title.className = 'ch-wishlist__recent-card-title';
  title.textContent = product.title;

  link.append(imageWrapper, title);
  item.append(link);

  return item;
}

async function renderEmptyRecommendations(container) {
  const recentGrid = container.querySelector('[data-ch-wishlist-recent-grid]');
  const recentHeading = container.querySelector('[data-ch-wishlist-recent-heading]');
  const fallback = container.querySelector('[data-ch-wishlist-empty-fallback]');
  if (!recentGrid || !fallback) return;

  recentGrid.hidden = true;
  if (recentHeading) recentHeading.hidden = true;
  fallback.hidden = true;
  recentGrid.replaceChildren();

  const viewedIds = readRecentlyViewedIds();
  if (!viewedIds.length) {
    fallback.hidden = false;
    return;
  }

  try {
    const url = new URL(`${ROOT}search/suggest.json`, window.location.origin);
    url.searchParams.set('q', viewedIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(viewedIds.length));

    const response = await fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to fetch recently viewed products');

    const data = await response.json();
    const products = data?.resources?.results?.products || [];
    const orderedProducts = viewedIds
      .map((id) => products.find((product) => String(product.id).endsWith(String(id))))
      .filter(Boolean);
    const cards = orderedProducts.map(createRecentProductCard).filter(Boolean);

    if (!cards.length) {
      fallback.hidden = false;
      return;
    }

    recentGrid.replaceChildren(...cards);
    recentGrid.hidden = false;
    if (recentHeading) recentHeading.hidden = false;
  } catch (_error) {
    fallback.hidden = false;
  }
}

function findItem(handle, variantId) {
  const variantKey = variantId ? String(variantId) : null;
  return state.items.find((item) => {
    if (variantKey && item.variantId) return String(item.variantId) === variantKey;
    return item.handle === handle;
  });
}

function setItems(items) {
  state.items = dedupeItems(items);
  updateUI();
  document.dispatchEvent(new CustomEvent('ch:wishlist:updated', { detail: { items: state.items } }));
}

function updateWishlistCounts() {
  const countText = state.items.length > 0 ? String(state.items.length) : '';

  document.querySelectorAll('[data-ch-wishlist-count]').forEach((counter) => {
    if (counter.textContent !== countText) counter.textContent = countText;
    counter.setAttribute('data-ch-wishlist-count', String(state.items.length));
    counter.closest('.header-actions__wishlist-count')?.classList.toggle('visually-hidden', state.items.length === 0);
  });
}

function queueWishlistCountUpdate() {
  if (state.countUpdateFrame) return;

  state.countUpdateFrame = requestAnimationFrame(() => {
    state.countUpdateFrame = null;
    updateWishlistCounts();
  });
}

function elementContainsWishlistCount(element) {
  if (!(element instanceof Element)) return false;
  return element.matches('[data-ch-wishlist-count]') || Boolean(element.querySelector('[data-ch-wishlist-count]'));
}

function observeWishlistCounts() {
  if (state.countObserver || !document.body) return;

  state.countObserver = new MutationObserver((mutations) => {
    if (
      mutations.some((mutation) => {
        const target = mutation.target;
        if (elementContainsWishlistCount(target instanceof Element ? target : target.parentElement)) return true;

        return Array.from(mutation.addedNodes).some((node) => {
          return elementContainsWishlistCount(node);
        });
      })
    ) {
      queueWishlistCountUpdate();
    }
  });

  state.countObserver.observe(document.body, {
    childList: true,
    characterData: true,
    subtree: true,
  });
}

function updateUI() {
  const handles = new Set(state.items.map((item) => item.handle).filter(Boolean));
  const variants = new Set(state.items.map((item) => item.variantId).filter(Boolean));

  document.querySelectorAll('[data-ch-wishlist-button]').forEach((button) => {
    const handle = button.dataset.productHandle;
    const variantId = button.dataset.variantId;
    const active = (variantId && variants.has(String(variantId))) || (handle && handles.has(handle));
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');

    const label = button.querySelector('[data-ch-wishlist-label]');
    if (label) label.textContent = active ? 'Remove from wishlist' : 'Add to wishlist';

    const productName = handle ? handle.replace(/-/g, ' ') : 'product';
    button.setAttribute('aria-label', active ? `Remove ${productName} from wishlist` : `Add ${productName} to wishlist`);
  });

  updateWishlistCounts();
}

async function requestWishlist(path, options = {}) {
  const response = await fetch(`${API_ROOT}${path}`, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  let data = null;
  try {
    data = await response.json();
  } catch (_error) {}

  if (!response.ok) {
    const error = new Error(data?.error || `Wishlist request failed: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return data || {};
}

async function fetchServerWishlist(options = {}) {
  const { preserveConfiguredItemsOnEmpty = false } = options;

  try {
    const data = await requestWishlist('/wishlist');
    if (data.isLoggedIn && Array.isArray(data.items)) {
      const config = readConfig();
      const serverItems = dedupeItems(data.items);
      const configuredItems = dedupeItems(config.metafieldItems || []);

      state.serverLoaded = true;
      window.CH_WISHLIST_CONFIG = { ...config, isLoggedIn: true };

      if (preserveConfiguredItemsOnEmpty && !serverItems.length && configuredItems.length) {
        setItems(configuredItems);
        return;
      }

      setItems(serverItems);
    }
  } catch (_error) {
    const config = readConfig();
    if (config.isLoggedIn && Array.isArray(config.metafieldItems)) {
      setItems(config.metafieldItems);
    }
  }
}

async function migrateGuestWishlist() {
  const localItems = readLocalItems();
  if (!localItems.length) return;

  try {
    const data = await requestWishlist('/wishlist-migrate', {
      method: 'POST',
      body: JSON.stringify({ items: localItems }),
    });
    localStorage.removeItem(STORAGE_KEY);
    if (Array.isArray(data.items)) {
      window.CH_WISHLIST_CONFIG = { ...readConfig(), isLoggedIn: true };
      setItems(data.items);
    } else {
      await fetchServerWishlist();
    }
  } catch (_error) {
    setItems(localItems);
  }
}

async function persistToggle(item, shouldAdd) {
  const config = readConfig();

  if (!config.isLoggedIn) {
    const nextItems = shouldAdd
      ? [...state.items, item]
      : state.items.filter((current) => itemKey(current) !== itemKey(item) && current.handle !== item.handle);
    setItems(nextItems);
    writeLocalItems(nextItems);
    return;
  }

  await requestWishlist('/wishlist', {
    method: shouldAdd ? 'POST' : 'DELETE',
    body: JSON.stringify({ variantId: item.variantId }),
  });
}

async function saveCartForLater(button) {
  if (button.getAttribute('aria-busy') === 'true') return;
  const item = normalizeItem({ handle: button.dataset.productHandle, productId: button.dataset.productId, variantId: button.dataset.variantId });
  if (!item?.handle || !item.variantId) return;
  button.setAttribute('aria-busy', 'true');
  const status = button.closest('.ch-cart-line-actions')?.querySelector('[role="status"]');
  try {
    if (!findItem(item.handle, item.variantId)) {
      await persistToggle(item, true);
      setItems(dedupeItems([...state.items, item]));
    }
    const component = button.closest('cart-items-component');
    const rows = Array.from(component.querySelectorAll('tr[data-key]'));
    const index = rows.findIndex((row) => row.dataset.key === button.dataset.lineKey);
    if (index >= 0) await component.onLineItemRemove(index + 1);
  } catch (error) {
    if (status) status.textContent = button.dataset.error;
  } finally {
    button.removeAttribute('aria-busy');
  }
}

async function toggleButton(button) {
  const item = normalizeItem({
    handle: button.dataset.productHandle,
    productId: button.dataset.productId,
    variantId: button.dataset.variantId,
  });
  if (!item?.handle || !item?.variantId || button.getAttribute('aria-busy') === 'true') return;

  const existing = findItem(item.handle, item.variantId);
  const shouldAdd = !existing;
  const previousItems = [...state.items];
  const optimisticItems = shouldAdd
    ? dedupeItems([...state.items, item])
    : state.items.filter((current) => itemKey(current) !== itemKey(item) && current.handle !== item.handle);

  button.setAttribute('aria-busy', 'true');
  setItems(optimisticItems);

  try {
    await persistToggle(item, shouldAdd);
    if (readConfig().isLoggedIn) await fetchServerWishlist();
    document.dispatchEvent(new CustomEvent('ch:wishlist:product-removed', { detail: shouldAdd ? null : item }));
  } catch (error) {
    setItems(previousItems);
    if (error.status === 401) {
      window.CH_WISHLIST_CONFIG = { ...readConfig(), isLoggedIn: false };
      await persistToggle(item, shouldAdd);
    }
  } finally {
    button.removeAttribute('aria-busy');
  }
}

async function renderWishlistCard(item, sectionId, quickAddStyle, mobileQuickAddStyle) {
  if (!item.handle) return null;

  const url = new URL(`${ROOT}products/${item.handle}`, window.location.origin);
  url.searchParams.set('section_id', sectionId || CARD_SECTION_ID);
  if (item.variantId) url.searchParams.set('variant', item.variantId);

  const response = await fetch(url.toString(), { credentials: 'same-origin' });
  if (!response.ok) return null;

  const html = await response.text();
  const documentFragment = new DOMParser().parseFromString(html, 'text/html');
  const card = documentFragment.querySelector('.ch-wishlist-rendered-card');
  if (!card) return null;

  const productCard = card.querySelector('product-card');
  const desktopStyle = quickAddStyle || 'footer';
  const mobileStyle = mobileQuickAddStyle === 'inherit' || !mobileQuickAddStyle ? desktopStyle : mobileQuickAddStyle;
  productCard?.setAttribute('data-quick-add-style', desktopStyle);
  productCard?.setAttribute('data-mobile-quick-add-style', mobileStyle);

  const listItem = document.createElement('li');
  listItem.className = 'ch-wishlist__item';
  listItem.dataset.productHandle = item.handle;
  listItem.dataset.variantId = item.variantId || '';
  listItem.append(card);
  preserveSectionStyles(documentFragment, listItem);
  return listItem;
}

async function renderWishlistPage(container) {
  const grid = container.querySelector('[data-ch-wishlist-grid]');
  const empty = container.querySelector('[data-ch-wishlist-empty]');
  const status = container.querySelector('[data-ch-wishlist-status]');
  const addAll = container.querySelector('[data-ch-wishlist-add-all]');
  const panel = container.querySelector('[data-ch-wishlist-panel]');
  const sectionId = container.dataset.cardSectionId || CARD_SECTION_ID;
  const quickAddStyle = container.dataset.cardQuickAddStyle || 'footer';
  const mobileQuickAddStyle = container.dataset.cardMobileQuickAddStyle || 'inherit';

  if (!grid || !empty || !status) return;

  status.hidden = false;
  grid.hidden = true;
  empty.hidden = true;
  if (panel) panel.hidden = true;
  grid.replaceChildren();

  if (!state.items.length) {
    status.hidden = true;
    empty.hidden = false;
    if (addAll) addAll.hidden = true;
    await renderEmptyRecommendations(container);
    return;
  }

  const currentItems = [...state.items];
  const renderedItems = await Promise.all(
    currentItems.map(async (item) => ({
      item,
      card: await renderWishlistCard(item, sectionId, quickAddStyle, mobileQuickAddStyle),
    }))
  );
  const validRenderedItems = renderedItems.filter(({ card }) => Boolean(card));
  const validItems = validRenderedItems.map(({ item }) => item);
  const prunedItems = renderedItems.filter(({ card }) => !card).map(({ item }) => item);

  if (!itemsAreSame(state.items, currentItems)) return;

  if (!itemsAreSame(state.items, validItems)) {
    state.items = dedupeItems(validItems);
    persistPrunedItems(prunedItems);
    document.dispatchEvent(new CustomEvent('ch:wishlist:pruned', { detail: { items: prunedItems } }));
  }

  grid.replaceChildren(...validRenderedItems.map(({ card }) => card));
  status.hidden = true;
  grid.hidden = validItems.length === 0;
  empty.hidden = validItems.length > 0;
  if (addAll) addAll.hidden = validItems.length === 0;
  if (panel) panel.hidden = validItems.length === 0;
  if (validItems.length === 0) await renderEmptyRecommendations(container);
  updateUI();
  refreshAliReviewsStarRatings(grid);
}

async function addWishlistToCart(container) {
  const button = container.querySelector('[data-ch-wishlist-add-all]');
  const renderedVariantIds = Array.from(container.querySelectorAll('.ch-wishlist__item[data-variant-id]'))
    .map((item) => item.dataset.variantId)
    .filter(Boolean);
  const variantIds = renderedVariantIds.length
    ? renderedVariantIds
    : state.items.map((item) => item.variantId).filter(Boolean);
  const items = Array.from(new Set(variantIds)).map((id) => ({ id, quantity: 1 }));
  if (!items.length || button?.getAttribute('aria-busy') === 'true') return;

  const sectionIds = getCartSectionIds();
  button?.setAttribute('aria-busy', 'true');
  if (button) button.disabled = true;
  const standardUpdate = beginCartLinesUpdate(container, {
    action: 'add',
    context: 'product',
    lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
    detail: { source: 'ch-wishlist', sourceId: 'ch-wishlist', suppressDrawerOpen: true },
  });
  try {
    let addResponse = {};
    try {
      addResponse = await addCartItemsInBatch(items, sectionIds);
    } catch (error) {
      if (!shouldRetryCartAddIndividually(error)) throw error;
      addResponse = await addCartItemsOneAtATime(items, sectionIds);
    }

    const cartResponse = await fetch(`${ROOT}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    const cart = cartResponse.ok ? await cartResponse.json() : {};
    if (!cartResponse.ok) throw new Error(`Unable to refresh the cart (${cartResponse.status}).`);

    standardUpdate.resolve(cart, {
      sections: addResponse.sections || {},
      itemCount: cart.item_count || 0,
    });

    await openCartDrawer();
  } catch (error) {
    standardUpdate.reject(error);
    console.error(error);
  } finally {
    button?.removeAttribute('aria-busy');
    if (button) button.disabled = false;
  }
}

async function initWishlistPage(container) {
  await renderWishlistPage(container);
  document.addEventListener('ch:wishlist:updated', () => renderWishlistPage(container));
  container.querySelector('[data-ch-wishlist-add-all]')?.addEventListener('click', () => addWishlistToCart(container));
}

async function init() {
  const config = readConfig();
  const localItems = readLocalItems();
  const metafieldItems = dedupeItems(config.metafieldItems || []);
  if (document.body.classList.contains('template-index')) {
    refreshHomepageAliReviewsNearViewport();
  } else {
    refreshAliReviewsStarRatings(document);
  }
  observeWishlistCounts();
  setItems(config.isLoggedIn ? metafieldItems : localItems);

  document.addEventListener('click', (event) => {
    const saveButton = event.target.closest?.('[data-ch-save-for-later]');
    if (saveButton) {
      event.preventDefault();
      saveCartForLater(saveButton);
      return;
    }
    const button = event.target.closest?.('[data-ch-wishlist-button]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggleButton(button);
  });

  document.querySelectorAll('[data-ch-wishlist-page]').forEach((container) => initWishlistPage(container));

  if (config.isLoggedIn) {
    fetchServerWishlist({ preserveConfiguredItemsOnEmpty: metafieldItems.length > 0 }).then(async () => {
      if (readConfig().isLoggedIn && localItems.length) {
        await migrateGuestWishlist();
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
